// Web / Telegram: Chefi Super lives on the server (the account's plan), so it follows the account to every
// browser. Inside Telegram it is sold with Telegram Stars; in a browser through the web payment provider
// (Stripe as merchant of record): the server makes a checkout page, the app goes there, and Stripe sends
// the person back to /super?paid=1. Super is the only plan offered.
// Everything needs a signed-in account.
import i18n from '../i18n';
import { basePath } from './basePath';
import type { PlanProduct } from '../types';
import { fetchConfig, fetchMe, fetchPlan, starsInvoice, webPayConfig, webPayPortal, type WebPayConfig } from './auth';
import { telegram } from './telegram';

export const supportsPlanChanges = true;
export const canManageSubscription = (source?: string) => source === 'stripe';
export const startBilling = (): (() => void) => () => {};
export const SUPER_SKU = 'chefi.super.monthly';
/** Whether this build can sell plans (it still needs the provider configured and an account). */
export const storeAvailable = true;
/** Stars subscriptions are managed in Telegram; web ones in the provider's portal (opened from the app). */
export const manageInApp = !telegram.isInside;
export const GIFT_MONTHS = [1, 3, 12] as const;
export type SuperOffer = { product: PlanProduct; price: string; sku?: string; interval?: 'month' | 'year'; offerToken?: string };
export type GiftOffer = { product: PlanProduct; months: number; price: string };

const PRODUCTS: PlanProduct[] = ['super'];
let config: Promise<WebPayConfig> | null = null;
const webConfig = () => (config ??= webPayConfig().catch(() => ({ provider: null }) as WebPayConfig));

/** The plans on sale here. Stars show their price; the web provider shows it (with tax) in its checkout. */
export async function loadOffers(): Promise<SuperOffer[]> {
  if (telegram.isInside) {
    const { stars } = await fetchConfig();
    return PRODUCTS.filter(p => stars[p]).map(p => ({ product: p, price: `${stars[p]} ⭐` }));
  }
  const c = await webConfig();
  return c.provider ? PRODUCTS.filter(p => c.offers.includes(p)).map(p => ({ product: p, price: '' })) : [];
}

export async function loadGiftOffers(): Promise<GiftOffer[]> {
  if (telegram.isInside) {
    const { stars } = await fetchConfig();
    return PRODUCTS.flatMap(p => GIFT_MONTHS.map(months => ({ product: p, months, price: stars[p] ? `${stars[p] * months} ⭐` : '' }))).filter(g => g.price);
  }
  const c = await webConfig();
  return c.provider ? PRODUCTS.flatMap(p => GIFT_MONTHS.filter(m => c.offers.includes(`gift:${p}:${m}`)).map(months => ({ product: p, months, price: '' }))) : [];
}

/** The server's answer: does the signed-in account have Super right now (own plan or a seat)? */
export async function checkEntitlement(): Promise<boolean> {
  const user = await fetchMe().catch(() => null);
  return !!user?.super && (user.super.until === null || user.super.until > Date.now());
}

async function waitFor(check: () => Promise<boolean>): Promise<boolean> {
  for (let i = 0; i < 15; i++) {
    if (await check().catch(() => false)) return true;
    await new Promise(r => setTimeout(r, 2000));
  }
  return false;
}

/**
 * Pays with Stars inside Telegram (true once Telegram says paid). On the web it leaves for the provider's
 * checkout page and never resolves: the person comes back to /super?paid=1, where the screen waits for
 * the server to record the payment.
 */
async function pay(starsOf: () => Promise<{ url: string }>, offer: string): Promise<boolean> {
  if (telegram.isInside) return (await telegram.openInvoice((await starsOf()).url)) === 'paid';
  window.location.assign(`/${basePath ? `${basePath}/` : ''}checkout?offer=${encodeURIComponent(offer)}`);
  return new Promise<boolean>(() => {});
}

/** Subscribes to a plan; an existing web subscription is switched instead (the provider prorates it). */
export async function buySuper(offer: SuperOffer): Promise<boolean> {
  const has = async () => (await fetchMe())?.super?.plan === offer.product;
  return (await pay(() => starsInvoice(i18n.language, offer.product), offer.product)) && waitFor(has);
}

/** Buys a gift; true once its code exists (it then shows in the gift list). */
export async function buyGift(product: PlanProduct, months: number): Promise<boolean> {
  const before = (await fetchPlan()).gifts.length;
  if (!(await pay(() => starsInvoice(i18n.language, product, months), `gift:${product}:${months}`))) return false;
  return waitFor(async () => (await fetchPlan()).gifts.length > before);
}

export const restoreSuper = checkEntitlement;

/** Opens the web provider's portal (payment method, invoices, cancel). False when there is no web subscription. */
export async function manageSubscription(): Promise<boolean> {
  if (telegram.isInside) return false;
  try {
    telegram.openLink((await webPayPortal()).url);
    return true;
  } catch {
    return false;
  }
}
